import React, { useState } from 'react';
import { Braces } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import Tooltip from '../ui/Tooltip';
import {
  MESSAGE_VARIABLE_CHIPS,
  type MessageVariableGroup,
} from '../../utils/messageVariables';

interface VariableChipsProps {
  onInsert: (token: string) => void;
  /** When false, project tokens are muted / non-insertable in compose. In templates, keep true. */
  projectEnabled?: boolean;
  className?: string;
  /** `compact` — sidebar rail with tabs; `default` — stacked groups for modals. */
  variant?: 'default' | 'compact';
}

const GROUP_ORDER: MessageVariableGroup[] = ['lead', 'me', 'project', 'other'];

const GROUP_TITLE_KEYS: Record<MessageVariableGroup, string> = {
  lead: 'messages.varGroupLead',
  me: 'messages.varGroupMe',
  project: 'messages.varGroupProject',
  other: 'messages.varGroupOther',
};

const groupTabClass = (active: boolean, disabled = false) =>
  `shrink-0 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
    disabled
      ? 'cursor-not-allowed bg-gray-100/80 text-gray-400 border border-dashed border-gray-300/80'
      : active
        ? 'bg-primary-600 text-white shadow-sm'
        : 'bg-white/80 text-gray-600 hover:bg-gray-100 border border-gray-200/80'
  }`;

const chipClass = (disabled: boolean, compact: boolean) =>
  `rounded-md font-medium transition-colors ${
    compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'
  } ${
    disabled
      ? 'cursor-not-allowed bg-gray-50 text-gray-300'
      : 'bg-white text-gray-700 border border-gray-200/80 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700'
  }`;

const VariableChips: React.FC<VariableChipsProps> = ({
  onInsert,
  projectEnabled = true,
  className = '',
  variant = 'default',
}) => {
  const { t } = useI18n();
  const compact = variant === 'compact';
  const [activeGroup, setActiveGroup] = useState<MessageVariableGroup>('lead');

  const renderChip = (token: string, labelKey: string, disabled: boolean) => {
    const tooltipKey = labelKey.replace('.var.', '.varTooltip.');
    const chip = (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onInsert(token)}
        className={chipClass(disabled, compact)}
      >
        {t(labelKey)}
      </button>
    );

    if (disabled) {
      return <React.Fragment key={token}>{chip}</React.Fragment>;
    }

    return (
      <Tooltip key={token} content={t(tooltipKey)}>
        <span className="inline-flex">{chip}</span>
      </Tooltip>
    );
  };

  const renderGroupChips = (group: MessageVariableGroup) => {
    const vars = MESSAGE_VARIABLE_CHIPS.filter((v) => v.group === group);
    const locked = group === 'project' && !projectEnabled;
    return (
      <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-1.5'}`}>
        {vars.map((v) => renderChip(v.token, v.labelKey, locked))}
      </div>
    );
  };

  if (compact) {
    const projectLocked = !projectEnabled;
    const activeLocked = activeGroup === 'project' && projectLocked;

    return (
      <div className={className}>
        <div className="flex items-center gap-1.5 min-w-0">
          <Braces className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
          <span className="text-xs font-semibold text-gray-800 truncate">
            {t('messages.variablesHelpTitle')}
          </span>
        </div>

        <div className="mt-2 space-y-3">
          <div
            className="flex flex-wrap gap-1"
            role="tablist"
            aria-label={t('messages.variablesHelpTitle')}
          >
            {GROUP_ORDER.map((group) => {
              const locked = group === 'project' && projectLocked;
              const tab = (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeGroup === group}
                  disabled={locked}
                  onClick={() => setActiveGroup(group)}
                  className={groupTabClass(activeGroup === group, locked)}
                >
                  {t(GROUP_TITLE_KEYS[group])}
                </button>
              );

              if (!locked) {
                return <React.Fragment key={group}>{tab}</React.Fragment>;
              }

              return (
                <Tooltip key={group} content={t('messages.varSelectProjectHint')}>
                  <span className="inline-flex">{tab}</span>
                </Tooltip>
              );
            })}
          </div>

          {activeLocked ? (
            <p className="text-[11px] text-gray-400 leading-snug">
              {t('messages.varSelectProjectHint')}
            </p>
          ) : (
            <div className="max-h-28 overflow-y-auto overscroll-contain pr-0.5">
              {renderGroupChips(activeGroup)}
            </div>
          )}

          <p className="text-[10px] text-gray-400 leading-snug">
            {t('messages.variablesInsertHint')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <p className="text-xs font-medium text-gray-500">{t('messages.variablesHelpTitle')}</p>
      {GROUP_ORDER.map((group) => {
        const vars = MESSAGE_VARIABLE_CHIPS.filter((v) => v.group === group);
        if (vars.length === 0) return null;
        const locked = group === 'project' && !projectEnabled;
        return (
          <div key={group}>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">
              {t(GROUP_TITLE_KEYS[group])}
              {locked ? ` · ${t('messages.varSelectProjectHint')}` : ''}
            </p>
            {renderGroupChips(group)}
          </div>
        );
      })}
    </div>
  );
};

export default VariableChips;
